/**
 * Единственное место переводов enum/кодов backend → русские тексты UI.
 * См. docs/architecture/02_ADMIN_ARCHITECTURE.md §6.5.
 */

export const userStatusLabels: Record<string, string> = {
  pending_verification: 'Ожидает верификации',
  active: 'Активный',
  suspended: 'Заблокирован',
  deleted: 'Удалён',
};

export const invitationStatusLabels: Record<string, string> = {
  active: 'Активно',
  completed: 'Состоялось',
  partially_completed: 'Частично состоялось',
  cancelled: 'Отменено',
  archived: 'В архиве',
};

export const invitationCategoryLabels: Record<string, string> = {
  movie: 'Кино',
  cafe: 'Кафе',
  restaurant: 'Ресторан',
  walk: 'Прогулка',
  sport: 'Спорт',
  concert: 'Концерт',
  bar: 'Бар',
  outdoor: 'Активный отдых',
  exhibition: 'Выставка',
  other: 'Другое',
};

export const paymentTypeLabels: Record<string, string> = {
  i_pay: 'Я плачу',
  split: 'Пополам',
  each_pays: 'Каждый за себя',
  guest_pays: 'Приглашённый платит',
};

export const reportStatusLabels: Record<string, string> = {
  open: 'Открыта',
  reviewed: 'Рассмотрена',
  dismissed: 'Отклонена',
  upheld: 'Подтверждена',
};

export const reportCategoryLabels: Record<string, string> = {
  spam: 'Спам',
  inappropriate_content: 'Недопустимый контент',
  scam: 'Мошенничество',
  harassment: 'Домогательство',
  fake_profile: 'Фейковый профиль',
  other: 'Другое',
};

export const photoStatusLabels: Record<string, string> = {
  pending: 'На проверке',
  approved: 'Одобрено',
  rejected: 'Отклонено',
};

export const matchStatusLabels: Record<string, string> = {
  pending: 'Ожидает',
  active: 'Активен',
  expired: 'Истёк',
  cancelled: 'Отменён',
  completed: 'Встреча состоялась',
};

export const genderLabels: Record<string, string> = {
  M: 'Мужской',
  F: 'Женский',
  Other: 'Не указан',
};

export const ratingEventTypeLabels: Record<string, string> = {
  meeting_confirmed: 'Подтверждённая встреча',
  meeting_no_show: 'Неявка на встречу',
  report_confirmed: 'Подтверждённая жалоба',
  partner_positive: 'Положительная оценка партнёра',
  partner_negative: 'Отрицательная оценка партнёра',
  manual_adjustment: 'Ручная корректировка',
  initial: 'Начальное значение',
};

/** Человекочитаемые названия действий audit-лога. */
export const auditActionLabels: Record<string, string> = {
  'auth.user_registered': 'Регистрация пользователя',
  'auth.otp_requested': 'Запрос SMS-кода',
  'auth.call_requested': 'Запрос авторизации звонком',
  'auth.call_check_init': 'Инициализация проверки звонка',
  'auth.logout': 'Выход из системы',
  'profile.updated': 'Профиль изменён',
  'profile.avatar_changed': 'Смена аватара',
  'profile.onboarding_completed': 'Онбординг завершён',
  'invitation.created': 'Приглашение создано',
  'invitation.archived': 'Приглашение в архиве',
  like: 'Лайк приглашения',
  dislike: 'Дизлайк приглашения',
  'like.removed': 'Лайк отозван',
  'like.rejected': 'Заявка отклонена автором',
  'match.created': 'Матч создан',
  'match.cancelled': 'Матч отменён',
  'chat.message_sent': 'Сообщение в чате',
  'meeting.confirm_attempt': 'Попытка подтверждения встречи',
  'meeting.completed': 'Встреча подтверждена',
  'rating.changed': 'Рейтинг изменён',
  'rating.manual_adjustment': 'Ручная корректировка рейтинга',
  'user.reported': 'Подана жалоба',
  'user.account_deleted': 'Аккаунт удалён',
  'staff.login_success': 'Вход сотрудника',
  'staff.login_failed': 'Неудачный вход сотрудника',
  'staff.user_suspended': 'Блокировка пользователя',
  'staff.user_unsuspended': 'Разблокировка пользователя',
  'staff.photo_approved': 'Фото одобрено',
  'staff.photo_rejected': 'Фото отклонено',
  // Legacy-записи до фикса опечатки (feature/admin-v2)
  'staff.photo_rejectd': 'Фото отклонено',
  'staff.report_upheld': 'Жалоба подтверждена',
  'staff.report_dismissed': 'Жалоба отклонена',
  'staff.invitation_hidden': 'Приглашение скрыто',
  'staff.invitation_unhidden': 'Приглашение восстановлено',
};

export function auditActionLabel(action: string): string {
  return auditActionLabels[action] ?? action;
}

export function label(dict: Record<string, string>, key: string | null | undefined): string {
  if (key === null || key === undefined) return '—';
  return dict[key] ?? key;
}

/** ISO-строка → локальный формат "17.07.2026, 14:05". */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** ISO-строка даты → "17.07.2026". */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ru-RU');
}
